import { cssMerge } from '@volvo-cars/css/utils';
import type React from 'react';
import { forwardRef } from 'react';
import { ErrorMessage } from './error-message';
import { Hint } from './hint';
import type { TextLikeProps } from './types';
import { useIds } from './use-ids';

export interface TextInputProps extends TextLikeProps {
  /**
   * Regular expression that the input value must match.
   */
  pattern?: string;

  /**
   * Id of a `<datalist>` element with a list of predefined values to suggest to the user.
   */
  list?: string;

  /**
   * Specifies the visible width, in characters, of the input.
   */
  size?: number;

  /**
   * Minimum length of the input value.
   */
  minLength?: number;

  /**
   * Maximum length of the input value.
   */
  maxLength?: number;

  /**
   * Hint the browser about the appropriate virtual keyboard for the type of expected data.
   */
  inputMode?: 'none' | 'text' | 'numeric' | 'decimal';

  /**
   * Controls whether and how text input is automatically capitalized as it is entered by the user.
   */
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';

  /**
   * Controls whether the browser should automatically correct the text as it is being entered.
   */
  autoCorrect?: 'off' | 'on';

  /**
   * Controls whether the element may be checked for spelling errors.
   */
  spellCheck?: boolean;

  /**
   * What type of information to autocomplete in the input.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  autoComplete?:
    | 'off'
    | 'on'
    | 'bday'
    | 'bday-day'
    | 'bday-month'
    | 'bday-year'
    | 'name'
    | 'family-name'
    | 'given-name'
    | 'username'
    | 'one-time-code'
    | 'organization-title'
    | 'organization'
    | 'street-address'
    | 'address-line1'
    | 'address-line2'
    | 'address-line3'
    | 'address-level4'
    | 'address-level3'
    | 'address-level2'
    | 'address-level1'
    | 'country'
    | 'country-name'
    | 'postal-code'
    | 'cc-name'
    | 'cc-number'
    | 'cc-exp'
    | 'cc-exp-month'
    | 'cc-exp-year'
    | 'cc-csc'
    | 'cc-type'
    | 'transaction-currency'
    | 'transaction-amount'
    | 'language'
    | 'sex';

  /**
   * Inject an element into the input field, displayed at the start of the input.
   *
   * Only supported with the `top-label` variant.
   */
  contentBefore?: React.ReactNode;

  /**
   * Inject an element into the input field, displayed at the end of the input.
   */
  contentAfter?: React.ReactNode;

  /**
   * Default value of an uncontrolled input.
   */
  defaultValue?: string;

  /**
   * Value of the input.
   *
   * Makes the input controlled.
   */
  value?: string;

  /**
   * Fires immediately when the input’s value is changed by the user (for example, it fires on every keystroke).
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

/**
 * TextInput should be used when the information is known by the user.
 * It can be used by itself or in combination with any other inputs.
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  function TextInput(props, ref) {
    return <TextLikeInput {...props} type="text" ref={ref} />;
  },
);

type TextLikeInputProps = Omit<TextInputProps, 'inputMode' | 'autoComplete'> & {
  type: string;
  inputClassName?: string;
  autoComplete?: string;
  inputMode?:
    | 'none'
    | 'text'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search';
};

export const TextLikeInput = forwardRef<HTMLInputElement, TextLikeInputProps>(
  function TextLikeInput(
    {
      variant = 'top-label',
      hint,
      id,
      label,
      hidden,
      inputClassName,
      dir,
      errorMessage: errorMessageProp,
      lang,
      translate,
      slot,
      className,
      style,
      contentBefore,
      contentAfter,
      ...props
    }: TextLikeInputProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) {
    const { inputId, errorId, hintId, labelId } = useIds(id);

    // Hide error message and error state when input is disabled
    const errorMessage = !props.disabled ? errorMessageProp : undefined;
    const ariaInvalid = props['aria-invalid'] || !!errorMessage;

    const inputProps = {
      // non-empty placeholder is required for floating label variant CSS selectors
      placeholder: variant === 'floating-label' ? ' ' : undefined,
      ...props,
      ref,
      id: inputId,
      'aria-invalid': ariaInvalid ? true : undefined,
      'aria-errormessage': errorMessage ? errorId : undefined,
      'aria-describedby': [
        props['aria-describedby'],
        errorMessage && errorId,
        hint && hintId,
      ]
        .filter(Boolean)
        .join(' '),
      // If `aria-labelledby` is set, we need to explicitly include the label ID
      'aria-labelledby': props['aria-labelledby']
        ? [labelId, props['aria-labelledby']].filter(Boolean).join(' ')
        : undefined,
    };

    return (
      <div
        id={id}
        className={cssMerge(
          variant === 'floating-label'
            ? 'input-floating-label'
            : 'flex-col items-start gap-8',
          className,
        )}
        hidden={hidden}
        dir={dir}
        lang={lang}
        translate={translate}
        slot={slot}
        style={style}
      >
        <label
          htmlFor={inputId}
          id={props['aria-labelledby'] ? labelId : undefined}
        >
          {label}
        </label>

        {(variant === 'top-label' && contentBefore) || contentAfter ? (
          // oxlint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- The container delegates pointer focus to its nested native input; keyboard interaction stays on the input.
          <div
            className="input"
            data-input-delegate
            onClick={(event) => {
              const target = event.target;
              // Focus the input when clicking anywhere in the input container
              if (
                target instanceof Element &&
                !target.matches('input, button, button *') &&
                !window.getSelection()?.toString()
              ) {
                event.currentTarget.querySelector('input')?.focus();
              }
            }}
            aria-invalid={ariaInvalid ? true : undefined}
          >
            {variant === 'top-label' ? contentBefore : null}
            <input
              {...inputProps}
              className={cssMerge('bg-transparent', inputClassName)}
            />
            {contentAfter}
          </div>
        ) : (
          <input {...inputProps} className={inputClassName} />
        )}

        <ErrorMessage
          errorMessage={errorMessage}
          id={errorId}
          className={variant === 'floating-label' ? 'mt-8' : undefined}
        />

        {hint && (
          <Hint
            id={hintId}
            className={variant === 'floating-label' ? 'mt-8' : undefined}
          >
            {hint}
          </Hint>
        )}
      </div>
    );
  },
);
